import React from 'react';
import { Ring, Roll } from '../jarvis/Ring';
import { money, dollars, plural, StatusPill } from './shared';

const Kpi = ({ color, label, value, sub, right, testid }) => (
  <div className="gpc-kpi" style={{ '--kc': color }} data-testid={testid}>
    <i />
    <div><small>{label}</small><strong><Roll value={value} /></strong><em>{sub}</em></div>
    {right && <div className="gpc-kt">{right}</div>}
  </div>
);

const Chip = ({ label, value, sub, color, dashed, testid }) => (
  <div className={`gpc-chip${dashed ? ' proj' : ''}`} data-testid={testid}>
    <small>{label}</small><strong style={color ? { color } : undefined}>{value}</strong><em>{sub}</em>
  </div>
);

/* The 10-second read: verdict, projected finish, required pace, pipeline cover + supporting lines. */
export default function VerdictHero({ cc, live }) {
  const v = cc.verdict || {};
  const lm = cc.lodgements?.month || {};
  const am = cc.approvals?.month || {};
  const sm = cc.settlements?.month || {};
  const cover = cc.cover || {};
  const bd = cc.business_days || {};
  const hasTarget = lm.target > 0;
  const p = hasTarget ? lm.actual / lm.target : 0;
  const expectedP = hasTarget ? lm.expected_now / lm.target : 0;
  const met = hasTarget && lm.actual >= lm.target;
  const short = lm.projected_gap > 0;
  const ringTone = v.tone === 'grey' ? 'blue' : v.tone;
  const closed = !cc.is_current;

  const gapLine = !hasTarget ? 'No lodgement target set — add one in Set Goals.'
    : closed ? `${met ? 'Reached' : 'Missed'} the ${money(lm.target, 1)} target by ${money(Math.abs(lm.actual - lm.target), 1)}.`
    : met ? `${money(lm.actual - lm.target, 1)} above the ${money(lm.target, 1)} target with ${bd.left} business day${bd.left === 1 ? '' : 's'} to go`
    : `${money(lm.remaining, 1)} still to lodge · should be at ${Math.round(expectedP * 100)}% by today, we're at ${Math.round(p * 100)}%`;

  return (
    <section className={`gpj-panel gpc-hero v-${v.tone || 'grey'}`} data-testid="gpc-verdict">
      <div className="gpc-main">
        <span className="gpc-badge" data-testid="gpc-verdict-badge"><i />{v.label}</span>
        <div className="gpc-big" data-testid="gpc-verdict-headline">
          <strong><Roll value={dollars(lm.actual)} /></strong>
          <span className="lbl">lodged this month · {plural(lm.count || 0, 'file')}{hasTarget ? ` · ${Math.round(p * 100)}% of ${money(lm.target, 1)}` : ''}</span>
        </div>
        <p className="gpc-gap" data-testid="gpc-verdict-gap"><b>{gapLine}</b></p>
        {hasTarget && cc.is_current && (
          <p className="gpc-proj" data-testid="gpc-verdict-projection">
            <span className="tag">Projection</span>
            <span>At the current pace we finish at <b style={{ color: short ? 'var(--vc)' : 'var(--green)' }}>{money(lm.projected, 1)}</b> — {short ? `${money(lm.projected_gap, 1)} below` : `${money(Math.abs(lm.projected_gap), 1)} above`} target</span>
          </p>
        )}
        <p className="gpc-detail" data-testid="gpc-verdict-detail">{v.detail}</p>
        {hasTarget && cc.is_current && (
          <div className="gpc-chips">
            <Chip label="Projected finish · forecast" value={money(lm.projected, 1)} color={short ? 'var(--vc)' : 'var(--green)'} dashed sub={short ? `${money(lm.projected_gap, 1)} short of target at current pace` : `${money(Math.abs(lm.projected_gap), 1)} above target at current pace`} testid="gpc-chip-projected" />
            {met ? (
              <Chip label="Buffer above target" value={money(lm.actual - lm.target, 1)} color="var(--green)" sub={`${bd.left} business day${bd.left === 1 ? '' : 's'} still to add to it`} testid="gpc-chip-required" />
            ) : (
              <Chip label="Required per business day" value={money(lm.per_bd_required)} sub={`to close ${money(lm.remaining, 1)} in ${bd.left} day${bd.left === 1 ? '' : 's'}`} testid="gpc-chip-required" />
            )}
            <Chip label="Running pace vs plan" value={`${money(lm.per_bd_actual)}/day`} color={lm.per_bd_actual >= lm.per_bd_plan ? 'var(--green)' : 'var(--amber)'} sub={`plan ${money(lm.per_bd_plan)}/day · ${lm.per_bd_plan ? Math.round((lm.per_bd_actual / lm.per_bd_plan) * 100) : 0}% of plan`} testid="gpc-chip-pace" />
            {met ? (
              <Chip label="Pipeline ready to lodge" value={money(cover.ready_value)} sub={`${plural(cover.ready_count, 'file')} to carry momentum into next month`} testid="gpc-chip-cover" />
            ) : (
              <Chip label="Pipeline ready to lodge" value={money(cover.ready_value)} color={cover.covers_remaining ? 'var(--green)' : cover.cover_pct_of_remaining >= 50 ? 'var(--amber)' : 'var(--red)'} sub={`${plural(cover.ready_count, 'file')} · covers ${Math.round(cover.cover_pct_of_remaining || 0)}% of what's left`} testid="gpc-chip-cover" />
            )}
            <Chip label="Business days" value={`${bd.left} left`} sub={`day ${bd.done} of ${bd.total} · ${Math.round(((bd.done || 0) / (bd.total || 1)) * 100)}% of month elapsed`} testid="gpc-chip-days" />
          </div>
        )}
      </div>

      <div className="gpc-ringwrap">
        <Ring p={Math.min(p, 1)} size={200} value={hasTarget ? `${Math.round(p * 100)}%` : '—'} of={hasTarget ? `${money(lm.actual, 1)} of ${money(lm.target, 1)}` : money(lm.actual, 1)} tone={ringTone} label="Lodged vs monthly target"
          sub={!hasTarget ? 'Set a target in Set Goals' : met ? `Target met · <b>${money(lm.actual - lm.target, 1)}</b> over` : `Should be at <b>${Math.round(expectedP * 100)}%</b> by today`} testid="gpc-ring-lodged" />
      </div>

      <div className="gpc-side">
        <Kpi color="var(--blue)" label="Lodged this month" value={dollars(lm.actual)} sub={hasTarget ? `${Math.round(p * 100)}% of ${money(lm.target, 1)} · ${plural(lm.count || 0, 'file')}` : plural(lm.count || 0, 'file')} right={<StatusPill block={lm} testid="gpc-pill-lodged" />} testid="gpc-kpi-lodged" />
        <Kpi color="var(--cyan)" label="Approved this month" value={plural(am.count || 0, 'file')} sub={`${money(cc.approvals?.amount_month, 1)} · GCI ${money(cc.approvals?.gci_month)}${am.target ? ` · target ${Math.round(am.target)} files` : ''}`} right={<StatusPill block={am} testid="gpc-pill-approved" />} testid="gpc-kpi-approved" />
        <Kpi color="var(--green)" label="Settled this month" value={dollars(sm.actual)} sub={sm.target ? `${Math.round((sm.actual / sm.target) * 100)}% of ${money(sm.target, 1)} · ${money(sm.booked)} booked` : plural(sm.count || 0, 'file')} right={<StatusPill block={sm} testid="gpc-pill-settled" />} testid="gpc-kpi-settled" />
        <Kpi color={live?.pipeline_health?.over ? 'var(--red)' : 'var(--violet)'} label="In flight" value={money(live?.pipeline_health?.value, 1)} sub={`${plural(live?.pipeline_health?.count || 0, 'active file')}${live?.pipeline_health?.over ? ` · ${live.pipeline_health.over} over limit` : ' · all inside limits'}`} testid="gpc-kpi-inflight" />
      </div>
    </section>
  );
}
